import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

/**
 * The loan a commission claim is attached to.
 *
 * 🔴 ONE ladder, shared by the All Claims table and the claim detail, because
 * a claim carries TWO different loan facts and the order between them is not
 * obvious:
 *
 *  1. `loanNumber` — resolved from `loanId`, the real link. The per-payment
 *     suggestion emitter sets `loanId` and never sets `loanReference`, so this
 *     is what the overwhelming majority of claims actually have.
 *  2. `loanReference` — operator free text, and ALSO the scope label a
 *     recurring-pool roll-up writes ("J · August 2026 · 2 loans") precisely
 *     because such a claim spans many loans and has no single `loanId`.
 *  3. Neither — an em dash.
 *
 * Rendering (2) before (1) is what produced the reported symptom: every claim
 * on a loan showed "—" while the column that identified it sat populated.
 *
 * The link is gated on `Loans.Read` and HIDDEN rather than disabled when the
 * reader lacks it (the repo's convention), because the loan route is itself
 * permission-gated — an ungated link would be a door to a room the reader
 * cannot enter. The number still renders as text.
 */
export function ClaimLoan({
  loanId,
  loanNumber,
  loanReference,
}: {
  loanId?: string | null;
  loanNumber?: string | null;
  loanReference?: string | null;
}) {
  const { hasPermission } = useAuth();

  if (loanNumber) {
    return loanId && hasPermission("Loans.Read") ? (
      <Link
        to={`/portfolio-admin/loans/${loanId}`}
        className="font-mono text-gold-shadow hover:underline"
      >
        {loanNumber}
      </Link>
    ) : (
      <span className="font-mono">{loanNumber}</span>
    );
  }

  if (loanReference) return <span>{loanReference}</span>;

  return <span>—</span>;
}
